/**
 * The Dashboard's two idea cards, built like the AI usage cards: a
 * figure and a share on one line, one segmented bar, then a legend with the
 * counts.
 *
 * - Status: every idea, split into active, parked and archived.
 * - Verdict: every reading of the Verdict column (a verdict, In research or
 *   Failed). Its head also has the button that opens the scoring guide.
 *
 * The counts come from the ideas list, which counts each card under every
 * filter but its own. A legend entry is a link to the Ideas list filtered to
 * that part. `children` are further cards in the same row, after a rule;
 * `stacked` keeps the cards one above the other at any width.
 */

import type { ReactNode } from 'react';
import { CircleHelp } from 'lucide-react';

import type { Disposition, VerdictOutcome } from '@/api/types';
import { formatPct } from '@/ui/format';
import { SplitCard } from '@/ui/SplitCard';

import { DISPOSITION_LABEL, OUTCOMES, OUTCOME_STYLE } from './ideaUi';

const DISPOSITIONS: Disposition[] = ['active', 'parked', 'archived'];

const DISPOSITION_SEG: Record<Disposition, string> = {
  active: 'seg-ok',
  parked: 'seg-refused',
  archived: 'seg-other',
};

export const OUTCOME_SEG: Record<VerdictOutcome, string> = {
  go: 'seg-ok',
  conditional_go: 'seg-refused',
  no_go: 'seg-failed',
  researching: 'seg-flight',
  failed: 'seg-error',
};

export function MatrixTiles({
  counts,
  outcomeCounts,
  dispositionHref,
  outcomeHref,
  onOpenGuide,
  scoredNoun = 'scored',
  stacked = false,
  children,
}: {
  counts: Record<Disposition, number>;
  outcomeCounts: Record<VerdictOutcome, number>;
  /** Where a status legend entry leads (the Ideas list filtered to it). */
  dispositionHref: (value: Disposition) => string;
  /** Where a verdict legend entry leads. */
  outcomeHref: (value: VerdictOutcome) => string;
  onOpenGuide: () => void;
  /** What the verdict card's figure counts, e.g. "active scored" when scoped. */
  scoredNoun?: string;
  stacked?: boolean;
  children?: ReactNode;
}) {
  const ideas = DISPOSITIONS.reduce((sum, d) => sum + counts[d], 0);
  const scored = outcomeCounts.go + outcomeCounts.conditional_go + outcomeCounts.no_go;

  return (
    <div className="metric-area">
      <div className={`metric-row usage-summary${stacked ? ' is-stacked' : ''}`}>
        <SplitCard
          label="Ideas by status"
          className="matrix-card-status"
          figure={ideas}
          noun={ideas === 1 ? 'idea' : 'ideas'}
          aside={ideas > 0 && <span className="usage-card-of">{formatPct((counts.active / ideas) * 100)} active</span>}
          parts={DISPOSITIONS.map((d) => ({ key: d, label: DISPOSITION_LABEL[d], count: counts[d], seg: DISPOSITION_SEG[d] }))}
          linkFor={dispositionHref}
        />
        <span className="metric-divider" aria-hidden="true" />
        <SplitCard
          label="Ideas by verdict"
          className="matrix-card-verdict"
          figure={scored}
          noun={scoredNoun}
          aside={
            <span className="matrix-card-aside">
              {scored > 0 && <span className="usage-card-of">{formatPct((outcomeCounts.go / scored) * 100)} go</span>}
              <button
                type="button"
                className="btn btn-ghost btn-icon matrix-guide"
                aria-label="How ideas are scored"
                title="How ideas are scored"
                onClick={onOpenGuide}
              >
                <CircleHelp aria-hidden="true" />
              </button>
            </span>
          }
          parts={OUTCOMES.map((o) => ({ key: o, label: OUTCOME_STYLE[o].label, count: outcomeCounts[o], seg: OUTCOME_SEG[o] }))}
          linkFor={outcomeHref}
        />
        {children && <span className="metric-divider" aria-hidden="true" />}
        {children}
      </div>
    </div>
  );
}
